import {
  Entity,
  GroupEntity,
  RELATION_MEMBER_OF,
} from '@backstage/catalog-model';
import { groupRefOf } from './groupRef';

type EntityFilterQuery =
  Record<string, string | string[]> | Record<string, string | string[]>[];

/**
 * Narrow structural subset of CatalogApi (from @backstage/catalog-client) —
 * not a direct dependency of this package, only pulled in transitively via
 * @backstage/plugin-catalog-react, so this avoids an implicit dependency on
 * it. Any real catalogApiRef instance satisfies this shape. `filter` accepts
 * an array of filter objects (OR'd together), matching the real CatalogApi's
 * EntityFilterQuery — used below to push the leadership lookup server-side.
 */
export interface CatalogApiLike {
  getEntityByRef(ref: string): Promise<Entity | undefined>;
  getEntities(request: {
    filter?: EntityFilterQuery;
    fields?: string[];
  }): Promise<{ items: Entity[] }>;
  getEntitiesByRefs(request: {
    entityRefs: string[];
    fields?: string[];
  }): Promise<{ items: (Entity | undefined)[] }>;
}

const LEADERSHIP_ANNOTATION_KEYS = [
  'team/lead',
  'team/pm',
  'team/tech-lead',
  'team/architect',
];

/**
 * Every group ref this user genuinely belongs to — as a plain member
 * (RELATION_MEMBER_OF, which the catalog only ever derives from
 * spec.members) OR as a leader (team/lead|pm|tech-lead|architect
 * annotation). Mirrors the backend's getUserGroupRefs
 * (team-management-backend/src/services/catalogService.ts) — the catalog
 * never creates a relation for leadership alone, so a lead-only person on
 * some group would otherwise be invisible to any "which teams does this
 * user belong to" check (the PAT-779 add-member dialog trigger, and a
 * user's own "Teams" card both depend on this being complete).
 */
export async function resolveUserGroupRefs(
  userRef: string,
  catalogApi: CatalogApiLike,
): Promise<string[]> {
  const userEntity = await catalogApi
    .getEntityByRef(userRef)
    .catch(() => undefined);
  const relationRefsRaw = (userEntity?.relations ?? [])
    .filter(
      r =>
        r.type === RELATION_MEMBER_OF &&
        r.targetRef.toLowerCase().startsWith('group:'),
    )
    .map(r => r.targetRef);

  // Archiving a group only flips backstage.io/lifecycle — it doesn't touch
  // spec.members — so the catalog keeps deriving a relation for members of
  // an archived team. Filter those out here too, matching the leadership
  // path below and every other "active groups only" convention in this
  // plugin (TeamsPage, ParentGroupPicker, etc.).
  const relationGroupEntities = relationRefsRaw.length
    ? (
        await catalogApi.getEntitiesByRefs({
          entityRefs: relationRefsRaw,
          fields: ['metadata.annotations'],
        })
      ).items
    : [];
  const relationRefs = relationRefsRaw.filter(
    (_, i) =>
      (relationGroupEntities[i] as GroupEntity | undefined)?.metadata
        .annotations?.['backstage.io/lifecycle'] !== 'deprecated',
  );

  // Narrowed server-side to only groups naming this user in one of the four
  // leadership annotations, instead of fetching every Group in the org and
  // filtering client-side — an OR across annotation keys, expressed as one
  // filter object per key (EntityFilterQuery treats array entries as OR'd).
  const { items } = await catalogApi.getEntities({
    filter: LEADERSHIP_ANNOTATION_KEYS.map(key => ({
      kind: 'Group',
      [`metadata.annotations.${key}`]: userRef,
    })),
    fields: ['metadata.name', 'metadata.namespace', 'metadata.annotations'],
  });
  // Canonicalized via the shared groupRefOf — the same helper the backend's
  // getUserGroupRefs uses — so both sides produce the same (lowercased,
  // namespace-aware) ref for a given Group, matching the relation targetRefs
  // unioned in above.
  const leadershipRefs = (items as GroupEntity[])
    .filter(
      g => g.metadata.annotations?.['backstage.io/lifecycle'] !== 'deprecated',
    )
    .map(g => groupRefOf(g));

  return Array.from(new Set([...relationRefs, ...leadershipRefs]));
}
